import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSeasonChampion } from '../api/hooks';
import { Avatar } from '../components/Avatar';
import { BackIcon, CrownIcon, ShareIcon, TrophyIcon } from '../components/icons';
import { ErrorView } from '../components/StateViews';
import { backButton, haptics, isTelegram, openTelegramLink } from '../telegram';
import { track } from '../analytics';
import { plural, seasonLabel, shortDate } from '../format';

const SEASONS_PER_CHAPTER = 10;
const inChapter = (id: number) => ((id - 1) % SEASONS_PER_CHAPTER) + 1;

/**
 * Именная карточка чемпиона сезона (design_handoff_season_winners §2).
 * Spot-dark: золотой герой-блок дарк-онли. Пустые опциональные поля
 * (титул/цитата/награда/топ-3) схлопывают свои блоки целиком.
 */
export function SeasonChampionScreen() {
  const navigate = useNavigate();
  const { seasonId: raw } = useParams();
  const seasonId = Number(raw);
  const card = useSeasonChampion(seasonId);

  useEffect(() => {
    const back = () => navigate(-1);
    backButton.show(back);
    return () => backButton.hide(back);
  }, [navigate]);

  if (card.isLoading) {
    return (
      <div className="hof-screen" data-theme="dark">
        <div className="skeleton" style={{ height: 300, borderRadius: 20, margin: '6px 0 12px' }} />
        <div className="skeleton" style={{ height: 74, borderRadius: 13, marginBottom: 11 }} />
        <div className="skeleton" style={{ height: 120, borderRadius: 16 }} />
      </div>
    );
  }
  if (card.isError || !card.data) {
    return (
      <div className="hof-screen" data-theme="dark">
        <div className="subhead hof-head">
          <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Назад">
            <BackIcon />
          </button>
          <div className="hof-title">Зал славы</div>
        </div>
        <ErrorView message="Чемпион этого сезона ещё не определён" />
      </div>
    );
  }

  const { champion: c, top3, startsAt, endsAt } = card.data;
  const n = inChapter(card.data.seasonId);
  const period = startsAt && endsAt ? ` · ${shortDate(startsAt)} — ${shortDate(endsAt)}` : '';

  const share = () => {
    haptics.tap();
    track('champion_share', { seasonId: card.data.seasonId });
    const text = `${c.name} — чемпион, ${seasonLabel(card.data.seasonId)} · Straddle Poker`;
    const bot = import.meta.env.VITE_TELEGRAM_BOT_USERNAME as string | undefined;
    const link = bot ? `https://t.me/${bot}?startapp=hof_${card.data.seasonId}` : '';
    if (isTelegram) {
      const params = new URLSearchParams({ url: link, text });
      openTelegramLink(`https://t.me/share/url?${params.toString()}`);
    } else if (navigator.share) {
      void navigator.share({ text, ...(link ? { url: link } : {}) }).catch(() => undefined);
    } else {
      void navigator.clipboard?.writeText([text, link].filter(Boolean).join('\n'));
      haptics.success();
    }
  };

  return (
    <div className="hof-screen hof-screen--card" data-theme="dark">
      <div className="champ-hero">
        <img className="champ-mark" src="/logo.png" alt="" />
        <div className="champ-top">
          <button className="iconbtn" onClick={() => navigate(-1)} aria-label="Назад">
            <BackIcon />
          </button>
          <div className="champ-top-title">Сезон {n}</div>
          <button className="iconbtn" onClick={share} aria-label="Поделиться">
            <ShareIcon />
          </button>
        </div>
        <span className="hof-ring hof-ring--champ">
          <Avatar name={c.name} src={c.photoUrl} size={88} />
          <span className="hof-crown hof-crown--lg">
            <CrownIcon />
          </span>
        </span>
        <div className="champ-name">{c.name}</div>
        {c.title && <div className="champ-title">«{c.title}»</div>}
        <div className="champ-meta">
          Чемпион Сезона {n}
          {period}
        </div>
      </div>

      <div className="champ-body">
        <div className="champ-tiles">
          <div className="champ-tile">
            <b className="gold">{c.points}</b>
            <span>очков</span>
          </div>
          <div className="champ-tile">
            <b>{c.tournaments}</b>
            <span>{plural(c.tournaments, ['турнир', 'турнира', 'турниров'])}</span>
          </div>
          <div className="champ-tile">
            <b>{c.wins}</b>
            <span>{plural(c.wins, ['победа', 'победы', 'побед'])}</span>
          </div>
        </div>

        {c.reward && (
          <div className="champ-award">
            <TrophyIcon />
            <div>
              <div className="champ-award-title">Награда сезона</div>
              <p>{c.reward}</p>
            </div>
          </div>
        )}

        {c.quote && (
          <div className="champ-quote">
            <div className="hof-overline">Слово чемпиона</div>
            <p>«{c.quote}»</p>
          </div>
        )}

        {top3.length > 0 && (
          <div className="champ-top3">
            <div className="hof-overline">Итог сезона</div>
            {top3.map((r) => (
              <div key={r.playerId} className={`champ-top3-row${r.rank === 1 ? ' first' : ''}`}>
                <span className="rk">{r.rank}</span>
                <span className="nm">{r.name}</span>
                <span className="pts">{r.points}</span>
              </div>
            ))}
            <div className="hof-fade hof-fade--sm" />
          </div>
        )}
      </div>
    </div>
  );
}
